<template>
  <view-wrapper
    :prefix="prefix"
    name="Import Configurations"
    path="importconfigurations"
    :showCancel="true"
    addedText="Save"
    :addedFunc="editImportConfiguration"
    :hasChangesBeenMade="hasChangesBeenMade"
    :validation="validation"
    :loading="loading"
    :loadingText="loadingText"
  >
    <wrapper>
      <h3>Basic Settings</h3>
      <grafokett-input
        type="text"
        labelPlaceholder="Name"
        v-model="importConfiguration.name"
        :editable="true"
      />
      <grafokett-input
        type="text"
        labelPlaceholder="Header Line Number"
        v-model="importConfiguration.headersLineNumber"
        :editable="true"
      />
      <grafokett-input
        type="text"
        labelPlaceholder="Data Line Number"
        v-model="importConfiguration.dataStartsAtLine"
        :editable="true"
      />
      <drop-down
        class="flex-1"
        placeholder="Select delimiter"
        :value="{ name: importConfiguration.delimiter }"
        value-property="name"
        search-prop="name"
        :options="delimiters"
        @input="setDelimiter($event)"
      />
    </wrapper>
    <wrapper class="my-5">
      <h3 class="mb-6">Order Settings</h3>
      <div class="flex items-center mb-4">
        <checkbox v-model="importConfiguration.usesOrders"></checkbox>
        <label class="ml-2 text-sm font-bold">Uses Orders</label>
      </div>
      <grafokett-input
        type="text"
        labelPlaceholder="Order Reference Column"
        v-model="importConfiguration.orderReferenceColumn"
        :editable="true"
      />
      <grafokett-input
        type="text"
        labelPlaceholder="Default Order Data JSON"
        v-model="importConfiguration.defaultOrderDataJson"
        :editable="true"
      />
      <grafokett-input
        type="text"
        labelPlaceholder="Default Order Reference"
        v-model="importConfiguration.defaultOrderReference"
        :editable="true"
      />
    </wrapper>
    <wrapper class="mt-5">
      <h3>Article Settings</h3>
      <grafokett-input
        type="text"
        labelPlaceholder="Article Name Columns"
        v-model="importConfiguration.articleNameColumns"
        :editable="true"
      />
      <grafokett-input
        type="text"
        labelPlaceholder="External Id Column"
        v-model="importConfiguration.externalIdColumn"
        :editable="true"
      />
      <grafokett-input
        type="text"
        labelPlaceholder="Article Quantity Column"
        v-model="importConfiguration.articleQuantityColumn"
        :editable="true"
      />
    </wrapper>
    <wrapper class="my-5">
      <h3>Group Settings</h3>
      <grafokett-input
        type="text"
        labelPlaceholder="Groups column"
        v-model="importConfiguration.groupsColumn"
        :editable="true"
      />

      <span class="text-xs text-white" v-if="selectedGroups.length > 0"
        >Default Groups</span
      >
      <multiselect
        v-model="selectedGroups"
        :options="groups"
        :multiple="true"
        :close-on-select="true"
        placeholder="Default Groups"
        label="name"
        track-by="id"
      />
    </wrapper>

    <wrapper class="my-5">
      <div class="flex items-center justify-between mb-4">
        <h3>Order keys</h3>
        <button
          @click="addKey"
          type="button"
          class="flex items-center px-4 py-2 bg-transparent border-solid rounded cursor-pointer border-highlight text-highlight"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          <span>Add Key</span>
        </button>
      </div>
      <div
        v-for="(key, index) in importConfiguration.orderKeys"
        :key="index"
        class="flex items-center p-4 my-2 rounded bg-primary-light"
      >
        <grafokett-input
          :editable="true"
          v-model="key.key"
          labelPlaceholder="Key value"
        />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-6 h-6 ml-8 cursor-pointer text-highlight"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          @click="deleteKey(index)"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </div>
    </wrapper>
  </view-wrapper>
</template>

<script>
import GrafokettInput from "../../components/forms/Input.vue";
import Checkbox from "../../components/forms/Checkbox.vue";
import DropDown from "../../components/forms/DropDown.vue";
import Multiselect from "vue-multiselect";

export default {
  components: {
    GrafokettInput,
    Checkbox,
    DropDown,
    Multiselect,
  },
  data() {
    return {
      loading: false,
      loadingText: "",
      importConfiguration: {
        name: null,
        headersLineNumber: null,
        usesOrders: false,
        delimiter: null,
        articleNameColumns: null,
        orderReferenceColumn: null,
        defaultOrderDataJson: null,
        defaultGroupIds: null,
        groupsColumn: null,
        externalIdColumn: null,
        articleQuantityColumn: null,
        orderKeys: [],
      },
      selectedGroups: [],
      groups: [],
      delimiters: [{ name: "tab" }, { name: "semicolon" }],
      prefix: "",
    };
  },
  methods: {
    async getData() {
      this.loadingText = "Loading Import Configuration";
      this.loading = true;
      const { data: groupsData } = await this.$store.$api.get("/api/groups");

      if (groupsData)
        this.groups = groupsData.data.map(({ id, name }) => ({
          id,
          name,
        }));

      if (this.$route.params.id) {
        this.prefix = "Edit";
        const { data } = await this.$store.$api.get(
          `/api/importconfigurations/${this.$route.params.id}`
        );

        this.importConfiguration = data.data;
        const groupIds = this.importConfiguration.defaultGroupIds
          .split(",")
          .map(x => +x.trim());

        if (groupIds && groupIds.length) {
          this.selectedGroups = this.groups.filter(x =>
            groupIds.includes(x.id)
          );
        }
      } else {
        this.prefix = "Add";
      }

      this.loading = false;
    },
    validation() {
      const {
        headersLineNumber,
        dataStartsAtLine,
        articleNameColumn,
        name,
        orderKeys,
      } = this.importConfiguration;

      return (
        this.checkValue(name) &&
        this.checkValue(headersLineNumber) &&
        this.checkValue(dataStartsAtLine) &&
        this.checkValue(articleNameColumn) &&
        orderKeys.every(x => this.checkValue(x.key))
      );
    },
    checkValue(value) {
      const isValid =
        value !== null && value !== undefined && typeof value === "string"
          ? value !== "" && value !== " " && value.length > 0
          : true;

      return isValid;
    },
    hasChangesBeenMade() {
      return false;
    },
    async editImportConfiguration() {
      this.loading = true;

      this.importConfiguration.defaultGroupIds = this.selectedGroups
        .map(group => group.id)
        .join(",");

      try {
        await this.$store.$api.post(
          "/api/importconfigurations",
          this.importConfiguration
        );
      } catch (error) {
        throw error;
      } finally {
        this.loading = false;
      }
    },
    setDelimiter(value) {
      this.importConfiguration.delimiter = value.name;
    },
    deleteKey(index) {
      this.importConfiguration.orderKeys.splice(index, 1);
    },
    addKey() {
      this.importConfiguration.orderKeys.push({
        id: 0,
        key: "",
        importConfigurationsId: this.importConfiguration.id,
      });
    },
  },
  async mounted() {
    await this.getData();
  },
};
</script>

<style></style>
